import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  Paper,
  Chip,
} from '@mui/material';
import type { TicketDetailsTabProps } from './types';

const TicketDetailsTab: React.FC<TicketDetailsTabProps> = ({
  formData,
  onFormChange,
  users,
  adressen,
  milestones,
  webhooks,
  loadingWebhooks,
  ticket,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Titel"
        fullWidth
        required
        value={formData.titel}
        onChange={(e) => onFormChange({ ...formData, titel: e.target.value })}
      />

      <TextField
        label="Was muss getan werden?"
        fullWidth
        required
        multiline
        rows={3}
        value={formData.was}
        onChange={(e) => onFormChange({ ...formData, was: e.target.value })}
      />

      <TextField
        label="Wie soll es gemacht werden?"
        fullWidth
        multiline
        rows={3}
        value={formData.wie}
        onChange={(e) => onFormChange({ ...formData, wie: e.target.value })}
      />

      <TextField
        label="Wann (Frist)"
        type="datetime-local"
        fullWidth
        InputLabelProps={{ shrink: true }}
        value={formData.wann}
        onChange={(e) => onFormChange({ ...formData, wann: e.target.value })}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status}
            label="Status"
            onChange={(e) => onFormChange({ ...formData, status: e.target.value })}
          >
            <MenuItem value="Offen">Offen</MenuItem>
            <MenuItem value="In Bearbeitung">In Bearbeitung</MenuItem>
            <MenuItem value="Erledigt">Erledigt</MenuItem>
            <MenuItem value="Abgebrochen">Abgebrochen</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Priorität</InputLabel>
          <Select
            value={formData.prioritaet}
            label="Priorität"
            onChange={(e) => onFormChange({ ...formData, prioritaet: e.target.value })}
          >
            <MenuItem value="Niedrig">Niedrig</MenuItem>
            <MenuItem value="Mittel">Mittel</MenuItem>
            <MenuItem value="Hoch">Hoch</MenuItem>
            <MenuItem value="Dringend">Dringend</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <FormControl fullWidth>
        <InputLabel>Ausführender Mitarbeiter</InputLabel>
        <Select
          value={formData.zugewiesenAn || ''}
          label="Ausführender Mitarbeiter"
          onChange={(e) =>
            onFormChange({
              ...formData,
              zugewiesenAn: e.target.value ? Number(e.target.value) : undefined,
            })
          }
        >
          <MenuItem value="">
            <em>Ich selbst (Standard)</em>
          </MenuItem>
          {users.map((user) => (
            <MenuItem key={user.benutzerID} value={user.benutzerID}>
              {user.vollstaendigerName || user.benutzername}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>Kunde / Adresse</InputLabel>
        <Select
          value={formData.adresseID || ''}
          label="Kunde / Adresse"
          onChange={(e) =>
            onFormChange({
              ...formData,
              adresseID: e.target.value ? Number(e.target.value) : undefined,
            })
          }
        >
          <MenuItem value="">
            <em>Kein Kunde zugeordnet</em>
          </MenuItem>
          {adressen.map((adresse) => (
            <MenuItem key={adresse.adresseID} value={adresse.adresseID}>
              {adresse.firma
                ? `${adresse.firma} (${adresse.vollstaendigerName})`
                : adresse.vollstaendigerName}
              {adresse.istMandant && (
                <Chip label="Mandant" size="small" color="primary" sx={{ ml: 1 }} />
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {milestones.length > 0 && (
        <FormControl fullWidth>
          <InputLabel>Meilenstein</InputLabel>
          <Select
            value={formData.milestoneID || ''}
            label="Meilenstein"
            onChange={(e) =>
              onFormChange({
                ...formData,
                milestoneID: e.target.value ? Number(e.target.value) : undefined,
              })
            }
          >
            <MenuItem value="">
              <em>Kein Meilenstein</em>
            </MenuItem>
            {milestones.map((milestone) => (
              <MenuItem key={milestone.milestoneID} value={milestone.milestoneID}>
                {milestone.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Automation & Wiederholung */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="subtitle2" color="primary">
          Automation & Wiederholung
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.autoExecute}
              onChange={(e) => onFormChange({ ...formData, autoExecute: e.target.checked })}
            />
          }
          label="Automatisch ausführen (N8N Integration)"
        />

        {formData.autoExecute && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="textSecondary">
              N8N Automation
            </Typography>

            <Alert severity="info" sx={{ mb: 1 }}>
              Dieses Ticket wird automatisch an N8N gesendet, wenn die Frist erreicht ist.
            </Alert>

            <FormControl fullWidth size="small">
              <InputLabel>Webhook auswählen</InputLabel>
              <Select
                value={formData.webhookID || ''}
                onChange={(e) => onFormChange({ ...formData, webhookID: e.target.value ? Number(e.target.value) : undefined })}
                label="Webhook auswählen"
                disabled={loadingWebhooks}
              >
                <MenuItem value="">
                  <em>Kein Webhook</em>
                </MenuItem>
                {webhooks.map((webhook) => (
                  <MenuItem key={webhook.webhookID} value={webhook.webhookID}>
                    {webhook.name}
                    {webhook.beschreibung && (
                      <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                        - {webhook.beschreibung}
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {ticket && ticket.executedAt && (
              <Box>
                <Chip
                  label={`Ausgeführt am: ${new Date(ticket.executedAt).toLocaleString('de-DE')}`}
                  color="success"
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Box>
            )}

            {ticket && ticket.executionResult && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Antwort von N8N Automation:
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    borderLeft: 4,
                    borderLeftColor: 'success.main',
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                    }}
                  >
                    {ticket.executionResult}
                  </Typography>
                </Paper>
              </Box>
            )}

            {formData.autoExecute && !ticket?.executedAt && (
              <Alert severity="warning">
                Die Automation wird ausgeführt, sobald die Frist erreicht ist.
              </Alert>
            )}
          </Box>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.wiederholungAktiv}
              onChange={(e) => onFormChange({ ...formData, wiederholungAktiv: e.target.checked })}
            />
          }
          label="Wiederholung aktivieren"
        />

        {formData.wiederholungAktiv && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="textSecondary">
              Wiederholungs-Einstellungen
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
              <TextField
                label="Intervall"
                type="number"
                fullWidth
                value={formData.wiederholungIntervall || ''}
                onChange={(e) => onFormChange({ ...formData, wiederholungIntervall: e.target.value ? Number(e.target.value) : undefined })}
                inputProps={{ min: 1 }}
                helperText="z.B. 7 für alle 7 Tage"
              />

              <FormControl fullWidth>
                <InputLabel>Einheit</InputLabel>
                <Select
                  value={formData.wiederholungEinheit}
                  label="Einheit"
                  onChange={(e) => onFormChange({ ...formData, wiederholungEinheit: e.target.value })}
                >
                  <MenuItem value="Stunden">Stunden</MenuItem>
                  <MenuItem value="Tage">Tage</MenuItem>
                  <MenuItem value="Monate">Monate</MenuItem>
                  <MenuItem value="Jahre">Jahre</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Anzahl Wiederholungen"
              type="number"
              fullWidth
              value={formData.wiederholungAnzahl || ''}
              onChange={(e) => onFormChange({ ...formData, wiederholungAnzahl: e.target.value ? Number(e.target.value) : undefined })}
              inputProps={{ min: 1 }}
              helperText="Leer lassen für unbegrenzte Wiederholungen"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TicketDetailsTab;
